import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { FORM_ERROR, FormApi } from 'final-form';
import { Form } from 'react-final-form';
import createDecorator from 'final-form-focus';
import RedHexButton from '../../../components/hexButtons/RedHexButton';
import {
  required,
  composeValidators,
  invalidEmail,
} from '../../../utils/validators';
import FieldInput from './FieldInput';
import FieldTextArea from './FieldTextArea';
import Message from './Message';
import PrivacyPolicyCheckbox from './PrivacyPolicyCheckbox';


const StyledContactForm = styled.form`
  display: flex;
  align-items: center;
  flex-direction: column;
  position: relative;
  padding-bottom: 130px;
  width: 100%;
  
  @media(min-width: ${({ theme }) => theme.screenSizeWidth.large}) {
    padding-bottom: 0;
    width: 400px;
  }
`;

const SendButton = styled(RedHexButton)`
  position: absolute;
  height: 100px;
  width: 110px;
  bottom: -130px;
  left: 0;
  right: 0;
  margin: auto;
  
  @media(min-width: ${({ theme }) => theme.screenSizeWidth.large}) {
    top: 230px;
    right: -55px;
    left: unset;
    margin: 0;
  }
`;

// Wrapper to enable the desired position for send button without losing correct tabbing order
const Wrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 500px;
  z-index: 1;
  margin: 10px 0;
`;


interface Values {
  email: string,
  subject: string,
  message: string,
  privacyPolicyAccept: boolean,
}

const onSubmit = async (values: Values, form: FormApi<Values>) => {
  try {
    const response = await fetch(
      '/api/send-mail',
      {
        method: 'post',
        body: JSON.stringify(values),
      }
    );
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    // form.restart() couldn't be called while onSubmit is in progress,
    // setTimeout fixes this problem
    // @ts-ignore
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    setTimeout(() => { form.restart() }, 10);
    return {};
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn(e);
    return {
      [FORM_ERROR]: e.message,
    };
  }
};

const decorator = createDecorator<Values>();

const ContactForm = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { t } = useTranslation();

  return (
    <Form<Values>
      onSubmit={onSubmit}
      decorators={[decorator]}
      render={({
        handleSubmit, submitFailed, active, submitSucceeded, submitError,
      }) => {
        /** This conditions and useState hook is necessary to show message
         after correctly filled form. Otherwise the message would instantly disappear due
         to form.reset in onSubmit function.
         Additionally  this condition make the message disappear
         whenever user touches the form again.
         */
        if (active !== undefined || submitFailed) {
          setIsSubmitted(false);
        }

        if (submitSucceeded) {
          setIsSubmitted(true);
        }

        return (
          <StyledContactForm
            onSubmit={(values) => handleSubmit(values)}
          >
            <FieldInput
              name="email"
              placeholder={t('yourMail')}
              config={{ validate: composeValidators(required, invalidEmail) }}
            />
            <FieldInput
              name="subject"
              placeholder={t('subject')}
              config={{ validate: required }}
            />
            <Wrapper>
              <FieldTextArea
                name="message"
                placeholder={t('message')}
                config={{ validate: required }}
              />
              <PrivacyPolicyCheckbox
                name="privacyPolicyAccept"
                config={{ validate: required }}
              />
              <SendButton
                type="submit"
              >
                {t('send')}
              </SendButton>
            </Wrapper>
            <Message
              isVisible={isSubmitted}
              type="success"
            >
              {t('thankYouForMessage')}
            </Message>
            <Message
              isVisible={!!submitError}
              type="error"
            >
              {t('contactFormGeneralError')}
            </Message>
          </StyledContactForm>
        );
      }}
    />
  );
};

export default ContactForm;
